import { Export } from '@adgate/common/interfaces/export.interface';
import { Filter } from '@adgate/common/interfaces/filter.interface';
import { QuickSearch } from '@adgate/common/interfaces/quick-search.interface';
import { Component, Input, OnInit, AfterViewInit, ViewChild, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DialogService } from '@adgate/common/services/dialog-service';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatTooltip } from "@angular/material/tooltip";
import { MatRippleModule } from '@angular/material/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { Creativity } from '@adgate/modules/streaming/types/creativity.type';
import { CreativityFilter } from '@adgate/modules/streaming/types/creativity-filter.type';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { StreamingService } from '@adgate/modules/streaming/services/streaming-service';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { StateService } from '@adgate/common/services/state-service';
import { Privilege } from '@adgate/modules/admin/types/privilege.type';
import { Status } from '@adgate/common/types/status.type';
import { UploadDialogComponent, UploadDialogResult } from '@adgate/common/components/upload-dialog-component/upload-dialog-component';
import { filter, finalize, switchMap, throwError } from 'rxjs';
import { StreamingCreativityFiltersDialogComponent } from '@adgate/modules/streaming/dialogs/streaming-creativity-filters-dialog-component/streaming-creativity-filters-dialog-component';
import * as XLSX from 'xlsx';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { SpecCheckReportDialogComponent } from '@adgate/common/components/spec-check-report-dialog-component/spec-check-report-dialog-component';
import { environment } from '@env/environment';
import { Campaign } from '@adgate/modules/streaming/types/campaign.type';
import { StreamingCampaignEditDialogComponent } from '@adgate/modules/streaming/dialogs/streaming-campaign-edit-dialog-component/streaming-campaign-edit-dialog-component';

@Component({
  selector: 'streaming-creativity-grid-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatTooltip,
    ClipboardModule,
    MatRippleModule,
    MatSnackBarModule,
    ObjectDescPipe
  ],
  providers: [
    DatePipe
  ],
  templateUrl: './streaming-creativity-grid-component.html',
  styleUrl: './streaming-creativity-grid-component.scss',
})
export class StreamingCreativityGridComponent implements OnInit, AfterViewInit, QuickSearch, Export, Filter
{
  private dialogService: DialogService = inject(DialogService);
  private streamingService: StreamingService = inject(StreamingService);
  private authService: AuthenticationService = inject(AuthenticationService);
  private stateService: StateService = inject(StateService);
  private datePipe: DatePipe = inject(DatePipe);
  private _snackBar: MatSnackBar = inject(MatSnackBar);

  @Input() description: string = "Blank";
  @Input() filter: CreativityFilter | null = null;
  @Input() refreshOnInit: boolean = false;
  @Input() allowFiltering: boolean = false;

  private readonly FILTER_STORAGE_KEY = 'adgate.streaming.creativity.filter';

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = ['refcode', 'brand', 'agency', 'format', 'placementName', 'status', 'created', 'actions'];
  dataSource: MatTableDataSource<Creativity>;

  allowEditHistory: boolean = false;
  allowEditCreativity: boolean = false;

  constructor()
  {
    this.dataSource = new MatTableDataSource<Creativity>([]);
  }

  ngOnInit(): void
  {
    this.initPrivileges();

    // Load persistent filter if no filter was provided via @Input
    if (this.filter === null || this.filter === undefined) {
      this.filter = this.stateService.loadFilter(this.FILTER_STORAGE_KEY, CreativityFilter) ?? new CreativityFilter();
    }
    // After this point, this.filter is guaranteed to be a CreativityFilter instance

    if (this.refreshOnInit) {
      this.onRefresh();
    } else {
      this.dialogService.hideProgress();
    }
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getQuickSearchPlaceholder(): string
  {
    return "Search Creativities";
  }

  onQuickSearch(filter: string | null)
  {
    this.dataSource.filter = filter!;
  }

  onRefresh()
  {
    this.dialogService.showProgress();
    this.streamingService.getCreativities(this.filter!).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.hideProgress();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  showFilters(): boolean
  {
    return this.allowFiltering;
  }

  onFilter()
  {
    const dialogRef = this.dialogService.openAnyDialog(StreamingCreativityFiltersDialogComponent, {
      data: this.filter!,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if ( result ) {
        this.filter = result;
        this.stateService.saveFilter(this.FILTER_STORAGE_KEY, this.filter!);
        this.onRefresh();
      }
    });
  }

  onViewHistory(c: Creativity): void
  {
    this.dialogService.openHistoryDialog(
      Creativity.OBJECT_TYPE,
      c.id,
      `${c.brand} - ${c.placementName}`,
      c.status,
      this.allowEditHistory
    );
  }

  onEditCreativity(c: Creativity): void
  {
    // TODO: Implement edit dialog
  }

  allowDeleteCreativity(c: Creativity): boolean
  {
    return this.allowEditCreativity && (c.status.id == Status.STREAMING_CREATIVITY_CREATED || c.status.id == Status.STREAMING_CREATIVITY_SPECERROR);
  }

  onDeleteCreativity(c: Creativity): void
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Delete creativity "${c.referenceCode}"?`,
      {
        title: 'Delete Creativity',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'danger'
      }
    );

    dialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed === true),
      switchMap(() => {
        return this.streamingService.deleteCreativity(c.id);
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.onRefresh();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  allowUploadCreativity(c: Creativity): boolean
  {
    return this.allowEditCreativity && (c.status.id == Status.STREAMING_CREATIVITY_CREATED || c.status.id == Status.STREAMING_CREATIVITY_SPECERROR || c.status.id == Status.STREAMING_CREATIVITY_REJECTED);
  }

  onUploadCreativity(c: Creativity): void
  {
    let successMessage: string;

    const dialogRef = this.dialogService.openAnyDialog(UploadDialogComponent, {
      width: '600px',
      minWidth: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result:UploadDialogResult | null) => result !== null),
      switchMap((result:UploadDialogResult) => {
        if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_HTTP ) {
        return this.streamingService.uploadCreativity_HTTP(c.id, result.media);
        } else if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_ASPERA ) {
          this.dialogService.showProgress();
          return this.streamingService.uploadCreativity_ASPERA(c.id, result.media);
        } else {
          return throwError(() => new Error('Invalid upload method'));
        }
      }),
      finalize(() => {
        this.streamingService.getCreativities(this.filter!).subscribe(r => {
          this.dataSource.data = r.data;
        });
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dialogService.openInfoDialog(r.message);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onViewSpecCheckReport(c:Creativity)
  {
    const dialogRef = this.dialogService.openAnyDialog(SpecCheckReportDialogComponent,{
      data: {
        objectType: Creativity.OBJECT_TYPE,
        objectId: c.id,
        title: `${c.referenceCode}`,
        allowUpload: this.allowUploadCreativity(c),
        specsUrl: `${environment.helpCenterUrl}#specs-streaming`
      },
      width: '600px',
      minWidth: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result) => result != null)
    ).subscribe((result:'upload' | 'view-specs') => {
      if( result === 'upload' ) {
        this.onUploadCreativity(c);
      }
    });
  }

  onExport()
  {
    if (this.dataSource.data.length > 0) {
      this.dialogService.showProgress();
      const jsonData = this.dataSource.data.map((c: Creativity) => ({
        'Ref. Code': c.referenceCode,
        'Brand': c.brand,
        'Agency': c.agency?.description,
        'Office': c.commercialOffice?.description,
        'Format': c.type?.description,
        'Placement Name': c.placementName,
        'Status': c.status.description,
        'Created': this.datePipe.transform(c.dateCreated, 'd/M/yyyy')
      }));

      let filename = "creativity-export.xlsx";
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Creativities');
      XLSX.writeFile(wb, filename);
      this.dialogService.hideProgress();
    } else {
      this.dialogService.openErrorDialog("There are no creativities to export");
    }
  }

  onNewCampaign(): void
  {
    this.onEditCampaign( new Campaign() );
  }

  onEditCampaign(c: Campaign)
  {
    let successMessage: string;

    const dialogRef = this.dialogService.openAnyDialog(StreamingCampaignEditDialogComponent, {
      data: c,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: Campaign | null) => (result !== null) ),
      switchMap((result:Campaign) => {
        this.dialogService.showProgress();
        return this.streamingService.updateCampaign(c);
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => { 
        this.dialogService.openInfoDialog(r.message);
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onCopyRefCode(refCode: string)
  {
    this._snackBar.open(`${refCode} copied to clipboard`, '', { duration: 2000 });
  }

  getTableDataAccessor(): any
  {
    return (item: any, property: any) => {
      switch (property) {
        case 'refcode': return item.referenceCode;
        case 'agency': return item.agency.description;
        case 'format': return item.type.description;
        case 'status': return item.status.description;
        case 'created': return item.dateCreated;
        default: return item[property];
      }
    };
  }

  private initPrivileges(): void
  {
    let decodedToken = this.authService.decodeToken(this.stateService.data.userToken);
    this.allowEditHistory = this.authService.checkPrivilegeOnToken(decodedToken, [Privilege.STRM_EDITHISTORY]);
    this.allowEditCreativity = this.authService.checkPrivilegeOnToken(decodedToken, [Privilege.STRM_UPDATECREATIVITY]);
  }
}
